import {
    Badge,
    ButtonDropdown,
    Card,
    CardBody,
    Col,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from 'reactstrap';
import { Button, Input } from '../../../components';
import { Controller, useForm } from 'react-hook-form';
import { InvoiceSearchDto, InvoicingDto, PagingDto } from '../../../types';
import { InvoiceStatus } from '../../../common/Data';
import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import Select, { createFilter } from 'react-select';
import Dropzone from 'react-dropzone';
import { Institution, DocumentUpload, AmsDocument } from '../../../common/Types';
import InvoiceForm from '../invoice/InvoiceForm';
import GenerateAnnualInvoicesForm from '../../billing/GenerateAnnualInvoicesForm';
import { generateYears } from '../../../common/Utils';
import { institutionService } from '../../../services/InstitutionService';
import { invoiceService } from '../../../services/InvoiceService';
import qs from 'qs';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { documentService } from '../../../services/DocumentService';
import styled from 'styled-components';
import { APIUriAms } from '../../../services/HttpHelper';

const DropZoneContainer = styled.section`
    border: dashed 5px #cccccc;
    > div {
        height: 80px;
    }
`;

const AllInvoicesTab = (props) => {
    const [addingCustomInvoice, setAddingCustomInvoice] = useState(false);
    const [customInvoice, setCustomInvoice] = useState<InvoicingDto>();
    const [viewingInvoices, setViewingInvoices] = useState<InvoicingDto[]>([]);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [total, setTotal] = useState(0);
    const [paging, setPaging] = useState<PagingDto>({
        pageNumber: 1,
        maxPageSize: null,
        pageSize: 10,
    });

    const [newDocument, setNewDocument] = useState<DocumentUpload>();
    const [showNewDocumentModal, setShowNewDocumentModal] = useState<boolean>(false);
    const toggleShowNewDocumentModal = () => setShowNewDocumentModal(!showNewDocumentModal);

    const [w9DropdownOpen, setW9DropdownOpen] = useState(false);
    const [newInvoiceDropdownOpen, setNewInvoiceDropdownOpen] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);

    const [search, setSearch] = useState<InvoiceSearchDto>({
        institutionId: null,
        invoiceNumber: null,
        invoiceType: null,
        paymentType: null,
        year: null,
        status: null,
        ...qs.parse(props.location.search.replace('?', '')),
        paging: paging,
    });

    const { register, handleSubmit, reset, control, setValue } = useForm<{
        institution: Institution | null;
        status: string;
        year: string;
    }>({
        defaultValues: {
            institution: null,
            status: search.status ? String(search.status) : '',
            year: search.year ? String(search.year) : '',
        }
    });

    const {
        register: registerIdSearch,
        handleSubmit: handleIdSubmit,
        reset: resetId,
        control: controlIdSearch
    } = useForm<{
        invoiceNumber: string;
    }>({
        defaultValues: {
            invoiceNumber: search.invoiceNumber ? String(search.invoiceNumber) : ''
        }
    });

    const refreshInvoices = async () => {
        try {
            const urlSearch = qs.parse(props.location.search.replace('?', ''));
            const response = await invoiceService.getInvoices({
                ...search,
                ...urlSearch,
                paging: paging,
            });
            setViewingInvoices(response.invoices);
            setTotal(response.total);
        } catch (error) {
            toast.error('Failed to refresh invoices');
        }
    };

    useEffect(() => {
        let isMounted = true;

        const getData = async () => {
            try {
                const urlSearch = qs.parse(props.location.search.replace('?', ''));
                const response = await invoiceService.getInvoices({
                    ...search,
                    ...urlSearch,
                    paging: paging,
                });

                if (isMounted) {
                    setViewingInvoices(response.invoices);
                    setTotal(response.total);
                }
            } catch (error) {
                if (isMounted) {
                    toast.error('Failed to get invoices');
                }
            }
        };

        getData();

        return () => {
            isMounted = false;
        };
    }, [setViewingInvoices, paging, search, props.location]);

    useEffect(() => {
        let isMounted = true;

        const getInstitutions = async () => {
            try {
                const response = await institutionService.getInstitutions();

                if (isMounted) {
                    setInstitutions(response);
                    const setInstitution = response.find(
                        (x) => x.institutionId.toString() === `${search.institutionId}`,
                    );
                    if (setInstitution) {
                        setValue('institution', setInstitution);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    toast.error('Failed to get institutions');
                }
            }
        };

        getInstitutions();

        return () => {
            isMounted = false;
        };
    }, [setInstitutions, setValue, search.institutionId]);

    const startNewDocument = () => {
        setNewDocument({
            fileUpload: null,
            dto: {
                documentId: 0,
                institutionId: null,
                programId: null,
                title: 'CAAHEP_W9',
                active: true,
                fileUrl: '',
                isAwardLetter: false,
            } as AmsDocument,
        });
        toggleShowNewDocumentModal();
    };

    const addStagedDoc = (dropData) => {
        if (newDocument && dropData && dropData[0]) {
            dropData.fileId = uuidv4();

            setNewDocument({
                fileUpload: dropData[0],
                dto: {
                    ...newDocument.dto,
                    fileUrl: dropData[0].path as string,
                } as AmsDocument,
            });
        }
    };

    const saveNewDocument = async () => {
        if (newDocument) {
            const toastId = toast.info('Saving & uploading document...');

            try {
                await documentService.saveAndUploadRelatedDocument(newDocument);
                toast.update(toastId, {
                    render: 'Document uploaded and saved successfully',
                    type: 'success',
                });
                toggleShowNewDocumentModal();
            } catch (error) {
                toast.update(toastId, {
                    render: 'Error uploading and saving document',
                    type: 'error',
                });
            }
        }
    };

    const onSubmit = (formData) => {
        const newSearch: InvoiceSearchDto = {
            ...search,
            invoiceNumber: null,
            status: formData.status ? Number(formData.status) : null,
            institutionId: formData.institution ? formData.institution.institutionId : null,
            year: formData.year || null,
            invoiceType: null,
        };

        resetId({
            invoiceNumber: ''
        });

        props.history.push(`/invoices?${qs.stringify(newSearch)}`);

        setSearch(newSearch);
    };

    const onIdSubmit = (formData) => {
        const newSearch: InvoiceSearchDto = {
            ...search,
            invoiceNumber: formData.invoiceNumber || null,
            status: null,
            institutionId: null,
            year: null,
            invoiceType: null,
        };

        reset({
            institution: null,
            status: '',
            year: '',
        });

        props.history.push(`/invoices?${qs.stringify(newSearch)}`);

        setSearch(newSearch);
    };

    const clearSearchForm = () => {
        setSearch({
            institutionId: null,
            invoiceType: null,
            invoiceNumber: null,
            paymentType: null,
            year: null,
            status: null,
            paging: paging,
        });
        reset({
            institution: null,
            status: '',
            year: '',
        });
        resetId({
            invoiceNumber: '',
        });
    };

    const addInvoice = () => {
        setAddingCustomInvoice(true);
    };

    const customInvoiceInstitutionChange = (institutionId) => {
        setCustomInvoice({
            id: 0,
            institutionId: +institutionId,
            institutionTitle: institutions.find((x) => x.institutionId === +institutionId)?.title ?? '',
            status: 3,
            invoiceDate: new Date(),
            invoiceNumber: '',
            programIds: '',
            programTitles: '',
            isDeleted: false,
            invoiceType: 2,
        } as InvoicingDto);
    };

    const getBadgeColor = (status: number) => {
        if (status === 1) return 'success';
        if (status === 4) return 'secondary';
        return 'warning';
    };

    return (
        <>
            <Card>
                <CardBody>
                    <div className="d-flex align-items-center mb-4" style={{ gap: '1rem' }}>
                        <h3 className="mb-0 mt-1">All Invoices</h3>
                        <div className="flex-grow-1"></div>
                        <ButtonDropdown
                            isOpen={w9DropdownOpen}
                            toggle={() => setW9DropdownOpen(!w9DropdownOpen)}
                        >
                            <DropdownToggle caret className="invoices-btn-outline">
                                CAAHEP W-9
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={(e) => { e.preventDefault(); startNewDocument(); }}>
                                    Upload
                                </DropdownItem>
                                <DropdownItem tag="a" href={`${APIUriAms}/document/download-w9`} target="_blank" rel="noopener noreferrer">
                                    Download
                                </DropdownItem>
                            </DropdownMenu>
                        </ButtonDropdown>
                        <ButtonDropdown
                            isOpen={newInvoiceDropdownOpen}
                            toggle={() => setNewInvoiceDropdownOpen(!newInvoiceDropdownOpen)}
                        >
                            <DropdownToggle caret color="info">
                                <i className="mdi mdi-plus" /> New Invoice
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => setShowGenerateModal(true)}>
                                    Annual Invoices
                                </DropdownItem>
                                <DropdownItem onClick={() => addInvoice()}>
                                    Custom Invoice
                                </DropdownItem>
                            </DropdownMenu>
                        </ButtonDropdown>
                    </div>
                    <Row className="mb-3">
                        <Col>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div style={{ width: '350px', flexShrink: 0 }}>
                                    <Controller
                                        control={control}
                                        name="institution"
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                className="w-100"
                                                placeholder="Select Institution"
                                                options={institutions}
                                                getOptionLabel={(option) => option?.title || ''}
                                                getOptionValue={(option) => option ? String(option.institutionId) : ''}
                                                isClearable={true}
                                                isSearchable={true}
                                                filterOption={createFilter({ ignoreAccents: false })}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '38px',
                                                    }),
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="status" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Status
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="status"
                                        render={({ field }) => (
                                            <Input {...field} type="select" style={{ width: 'auto', minWidth: '150px' }}>
                                                <option value="">Select Status</option>
                                                <option value={1}>Paid</option>
                                                <option value={2}>Partially Paid</option>
                                                <option value={3}>Unpaid</option>
                                                <option value={4}>Unsent</option>
                                            </Input>
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="year" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Year
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="year"
                                        render={({ field }) => (
                                            <Input {...field} type="select" style={{ width: 'auto', minWidth: '150px' }}>
                                                <option value="">Select Year</option>
                                                {generateYears().map((ct) => (
                                                    <option key={ct} value={ct}>
                                                        {ct}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    />
                                </div>
                                <div className="d-flex" style={{ gap: '0.5rem' }}>
                                    <Button type="submit">Search</Button>
                                    <Button color="link" onClick={() => clearSearchForm()}>
                                        Clear
                                    </Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Label>OR</Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form noValidate onSubmit={handleIdSubmit(onIdSubmit)} className="d-flex align-items-center" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="invoiceNumber" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Invoice Number
                                    </Label>
                                    <Controller
                                        control={controlIdSearch}
                                        name="invoiceNumber"
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type="text"
                                                style={{ width: '250px' }}
                                                placeholder="Invoice"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="d-flex" style={{ gap: '0.5rem' }}>
                                    <Button type="submit">Search</Button>
                                    <Button color="link" onClick={() => clearSearchForm()}>
                                        Clear
                                    </Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </CardBody>

                <CardBody>
                    <h3>Invoice Results</h3>
                </CardBody>
                <Table>
                    <thead>
                    <tr>
                        <td>Invoice Number</td>
                        <td>Institution</td>
                        <td>Invoice Date</td>
                        <td>Status</td>
                        <td>Fee per Program</td>
                        <td>Administrative Fee</td>
                        <td>Amount Due</td>
                        <td></td>
                    </tr>
                    </thead>
                    <tbody>
                    {viewingInvoices.map((vInvoice) => (
                        <tr key={vInvoice.id}>
                            <td><Link to={`/system-admin/invoice/${vInvoice.id}`}>{vInvoice.invoiceNumber}</Link></td>
                            <td>
                                <a href={`/institution/${vInvoice.institutionId}`}>{vInvoice.institutionTitle}</a>
                            </td>
                            <td>{new Date(vInvoice.invoiceDate || new Date()).toDateString()}</td>
                            <td>
                                <Badge color={getBadgeColor(vInvoice.status)}>
                                    {InvoiceStatus[vInvoice.status]}
                                </Badge>
                            </td>
                            <td>${vInvoice.professionListDeserialized?.length
                                ? Math.round((vInvoice.amount ?? 0) / vInvoice.professionListDeserialized.length)
                                : vInvoice.amount}</td>
                            <td>${vInvoice.lateFee ?? 0}</td>
                            <td>${vInvoice.dueAmount}</td>
                            <td>
                                <Link to={`/system-admin/invoice/${vInvoice.id}`}>View</Link>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                <CardBody>
                    <h4>Total: {total}</h4>
                    <Pagination size="sm" aria-label="Invoice Navigation">
                        <PaginationItem disabled={paging.pageNumber === 1}>
                            <PaginationLink
                                previous
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber - 1 })}
                            />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink
                                next
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber + 1 })}
                            />
                        </PaginationItem>
                    </Pagination>
                </CardBody>
            </Card>

            <Modal isOpen={addingCustomInvoice} toggle={() => setAddingCustomInvoice(!addingCustomInvoice)} size="lg">
                <ModalHeader toggle={() => setAddingCustomInvoice(!addingCustomInvoice)}>
                    Add Custom Invoice
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Select Institution</Label>
                        <Select
                            className="w-100"
                            placeholder="Select Institution"
                            options={institutions}
                            getOptionLabel={(option) => option.title}
                            getOptionValue={(option) => String(option.institutionId)}
                            isClearable={true}
                            isSearchable={true}
                            filterOption={createFilter({ ignoreAccents: false })}
                            onChange={(e) => customInvoiceInstitutionChange(e?.institutionId)}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '38px',
                                }),
                            }}
                        />
                    </FormGroup>

                    {customInvoice && (
                        <InvoiceForm
                            invoice={customInvoice}
                            onSave={async () => {
                                await refreshInvoices();
                                setCustomInvoice(undefined);
                                setAddingCustomInvoice(false);
                                toast.success('Custom invoice added successfully');
                            }}
                            onCancel={() => setAddingCustomInvoice(false)}
                        />
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showNewDocumentModal} toggle={toggleShowNewDocumentModal} className={`modal-lg`}>
                <ModalHeader>{`New Related Document`}</ModalHeader>
                {newDocument && (
                    <ModalBody>
                        <FormGroup className={'d-flex flex-column'}>
                            <Label>{`File Upload`}</Label>
                            {newDocument?.dto?.fileUrl && <span className={'mb-2'}>{newDocument.dto.fileUrl}</span>}
                            <Dropzone onDrop={(acceptedFiles) => addStagedDoc(acceptedFiles)} multiple={false}>
                                {({ getRootProps, getInputProps }) => (
                                    <DropZoneContainer>
                                        <div {...getRootProps()}>
                                            <input {...getInputProps()} />
                                            <p style={{ textAlign: 'center', lineHeight: '80px' }}>
                                                {`Drag & drop a file here, or click to select a file`}
                                            </p>
                                        </div>
                                    </DropZoneContainer>
                                )}
                            </Dropzone>
                        </FormGroup>
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button
                        color={'primary'}
                        className={'mr-2'}
                        onClick={() => saveNewDocument()}
                        type={'button'}
                    >{`Upload & Save`}</Button>
                    <Button
                        color={'link'}
                        onClick={() => toggleShowNewDocumentModal()}
                        type={'button'}
                    >{`Cancel`}</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showGenerateModal} toggle={() => setShowGenerateModal(!showGenerateModal)} size="lg">
                <ModalHeader toggle={() => setShowGenerateModal(!showGenerateModal)}>
                    Generate Annual Invoices
                </ModalHeader>
                <ModalBody>
                    <GenerateAnnualInvoicesForm onClose={() => setShowGenerateModal(false)} />
                </ModalBody>
            </Modal>
        </>
    );
};

export default AllInvoicesTab;
